import { useMemo } from 'react'
import { useCustomer, useLatestOpportunity, useMedicalRecord, useConsultant, useMyAccount } from 'hooks'
import { CustomerAppointment } from 'types'
import { format } from 'date-fns'

export interface CustomerProfileData {
  customer: {
    givenName: string
    familyName: string
    dateOfBirth: string
    email?: string
    phoneNumber?: string
    billingAddress?: {
      addressLine1?: string
      city?: string
      postalCode?: string
    }
  }
  consultant?: {
    email?: string
    gender?: string
    gid?: string
    name?: string
    phone?: string
  }
  appointment?: {
    type?: string
    timezone?: string
    date?: string
  }
  insurance?: {
    insuranceName?: string
    preliminaryInsuranceName?: string
  }
  hearingHistory?: {
    hasCurrentHearingTest?: string
    currentHearingTestAt?: string
    prescription?: string
    scaleOfPrescription?: string
    hearingIssueStartDate?: string
    previousExperience?: string
  }
  medicalHistory?: {
    hasEarInfection?: string
    hasEarSurgery?: string
    hasFluctuatingHearingLoss?: string
    hasSuddenHearingLoss?: string
    hasTinnitus?: string
    hasVeteranNoises?: string
    relevantMedication?: string
    allergies?: string
  }
  socialHistory?: {
    formerProfession?: string
    professionalStatus?: string
    hobbies?: string
    cosiBasicType1?: string
    cosiBasicRemark1?: string
    cosiBasicType2?: string
    cosiBasicRemark2?: string
  }
  deviceSelection?: {
    preferredDeviceType?: string
    desiredFeatures?: string
    discussedPlatformDevice?: string
    discussedPriceRange?: string
    smartphone?: string
  }
  comment?: string
}

interface UseCustomerProfileDataProps {
  appointment?: CustomerAppointment
  customerId?: string
}

const useCustomerProfileData = ({ appointment }: UseCustomerProfileDataProps) => {
  const { data: customer } = useCustomer()

  const { data: latestOpportunity } = useLatestOpportunity(customer?.salesforceId, {
    enabled: Boolean(customer?.salesforceId),
  })

  const { data: medicalRecord } = useMedicalRecord({
    customerGid: customer?.salesforceId,
    opportunityGid: latestOpportunity?.salesforceId,
  })

  const { data: consultant } = useConsultant({
    consultantSalesforceId: latestOpportunity?.ownerSalesforceId,
    options: {
      enabled: Boolean(latestOpportunity?.ownerSalesforceId),
    },
  })

  const { data: account } = useMyAccount()

  const customerProfileData = useMemo((): CustomerProfileData | null => {
    if (!customer) {
      return null
    }

    const formatDate = (date: Date | string) => {
      try {
        const dateObj = typeof date === 'string' ? new Date(date) : date

        return format(dateObj, 'yyyy-MM-dd')
      } catch (error) {
        return ''
      }
    }

    return {
      appointment: {
        date: appointment?.start_date,
        timezone: account?.timezone,
        type: appointment?.type?.appointment_type,
      },
      comment: medicalRecord?.background?.comment || '',
      consultant: {
        email: consultant?.email,
        gender: consultant?.gender,
        gid: consultant?.gid,
        name: `${consultant?.givenName} ${consultant?.familyName}`,
        phone: consultant?.phoneNumber,
      },
      customer: {
        billingAddress: {
          addressLine1: customer.billingAddress?.addressLine1,
          city: customer.billingAddress?.city,
          postalCode: customer.billingAddress?.postalCode,
        },
        dateOfBirth: formatDate(customer.dateOfBirth),
        email: customer.email,
        familyName: customer.familyName,
        givenName: customer.givenName,
        phoneNumber: customer.phoneNumber,
      },
      deviceSelection: {
        desiredFeatures: medicalRecord?.background?.desired_features,
        discussedPlatformDevice: medicalRecord?.background?.discussed_platform_device,
        discussedPriceRange: medicalRecord?.background?.discussed_price_range,
        preferredDeviceType: medicalRecord?.background?.preferred_device_type,
        smartphone: medicalRecord?.background?.smartphone,
      },
      hearingHistory: {
        currentHearingTestAt: medicalRecord?.background?.current_hearing_test_at,
        hasCurrentHearingTest: medicalRecord?.background?.has_current_hearing_test,
        hearingIssueStartDate: medicalRecord?.background?.hearing_issue_start_date,
        prescription: medicalRecord?.background?.prescription,
        previousExperience: medicalRecord?.background?.previous_experience,
        scaleOfPrescription: medicalRecord?.background?.scale_of_prescription,
      },
      insurance: {
        insuranceName: medicalRecord?.background?.insurance_name,
        preliminaryInsuranceName: medicalRecord?.background?.preliminary_insurance_name,
      },
      medicalHistory: {
        allergies: medicalRecord?.background?.allergies,
        hasEarInfection: medicalRecord?.background?.has_ear_infection,
        hasEarSurgery: medicalRecord?.background?.has_ear_surgery,
        hasFluctuatingHearingLoss: medicalRecord?.background?.has_fluctuating_hearing_loss,
        hasSuddenHearingLoss: medicalRecord?.background?.has_sudden_hearing_loss,
        hasTinnitus: medicalRecord?.background?.has_tinnitus,
        hasVeteranNoises: medicalRecord?.background?.has_veteran_noises,
        relevantMedication: medicalRecord?.background?.relevant_medication,
      },
      socialHistory: {
        cosiBasicRemark1: medicalRecord?.background?.cosi_basic_remark_1,
        cosiBasicRemark2: medicalRecord?.background?.cosi_basic_remark_2,
        cosiBasicType1: medicalRecord?.background?.cosi_basic_type_1,
        cosiBasicType2: medicalRecord?.background?.cosi_basic_type_2,
        formerProfession: medicalRecord?.background?.former_profession,
        hobbies: medicalRecord?.background?.lifestyle_notes,
        professionalStatus: medicalRecord?.background?.professional_status,
      },
    }
  }, [customer, appointment, medicalRecord, latestOpportunity, consultant])

  return customerProfileData
}

export { useCustomerProfileData }
